import { ReactNode, useEffect, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { Icon, IconName } from "./Icons";
import { Avatar } from "./ui";
import { SyncBadge } from "./SyncBadge";
import { STUDIO, useStore, openRfis, submittedCount, isClosed, bidsUnsealed } from "../lib/store";
import { logout, useSession } from "../lib/auth";
import { bidLevel, BID_LEVEL_LABEL } from "../lib/bidPermissions";

export const SECTIONS: { key: string; label: string; icon: IconName }[] = [
  { key: "dashboard", label: "Dashboard", icon: "dashboard" },
  { key: "invitation", label: "Invitation to Tender", icon: "mail" },
  { key: "timeline", label: "Tender Timeline", icon: "stages" },
  { key: "documents", label: "Drawings & Specs", icon: "layers" },
  { key: "contract", label: "Draft Contract", icon: "brief" },
  { key: "quotes", label: "Supply Quotations", icon: "folder" },
  { key: "bidders", label: "Bidders", icon: "users" },
  { key: "submissions", label: "Submissions", icon: "folder" },
  { key: "rfis", label: "RFIs", icon: "question" },
  { key: "results", label: "Bid Results", icon: "gavel" },
  { key: "alerts", label: "Email Alerts", icon: "mail" },
  { key: "team", label: "Team & Invites", icon: "users" },
];

/* The four a phone keeps under its thumb; the rest live behind "More". */
const MOBILE_TABS: { key: string; short: string; icon: IconName }[] = [
  { key: "dashboard", short: "Home", icon: "dashboard" },
  { key: "documents", short: "Drawings", icon: "layers" },
  { key: "bidders", short: "Bidders", icon: "users" },
  { key: "rfis", short: "RFIs", icon: "question" },
];

/* --- theme ----------------------------------------------------------- */
export function useTheme(): [string, () => void] {
  const [theme, setTheme] = useState(() => localStorage.getItem("bidroom.theme") || "light");
  useEffect(() => { document.documentElement.setAttribute("data-theme", theme); localStorage.setItem("bidroom.theme", theme); }, [theme]);
  return [theme, () => setTheme((t) => (t === "light" ? "dark" : "light"))];
}

export function Shell({ children }: { children: ReactNode }) {
  const { tid = "", section = "dashboard" } = useParams();
  const nav = useNavigate();
  const { tenders } = useStore();
  const tender = tenders.find((t) => t.id === tid) ?? tenders[0];
  const [theme, toggleTheme] = useTheme();
  const [switcher, setSwitcher] = useState(false);
  const [more, setMore] = useState(false);   // phone: the "everything else" sheet
  const session = useSession();
  const level = bidLevel(session);
  // per-tender team members are locked to their tender; studio-level tender
  // roles (tenderId "") switch tenders like the manager
  const isTeam = session?.role === "bidteam" && session.tenderId !== "";

  const rfiCount = tender ? openRfis(tender) : 0;
  const readyToOpen = tender ? isClosed(tender) && !bidsUnsealed(tender) && tender.bids.length > 0 : false;

  return (
    <div className="shell-grid" style={{ display: "grid", gridTemplateColumns: "var(--nav-w) 1fr", minHeight: "100vh" }}>
      {/* ---------------- sidebar ---------------- */}
      <aside style={{ position: "sticky", top: 0, height: "100vh", borderRight: "1px solid var(--line)", background: "var(--paper-2)", display: "flex", flexDirection: "column", padding: "20px 16px" }}>
        <button onClick={() => nav("/manage")} className="row" style={{ gap: 10, background: "none", border: "none", cursor: "pointer", padding: "4px 6px", marginBottom: 22 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: "var(--ink)", color: "var(--paper)", display: "grid", placeItems: "center" }}><Icon.logo width={20} /></div>
          <div style={{ textAlign: "left" }}>
            <div className="serif" style={{ fontSize: 17, lineHeight: 1 }}>{STUDIO.name}</div>
            <div className="kicker" style={{ fontSize: 9.5, marginTop: 3 }}>Bid Manager</div>
          </div>
        </button>

        {/* tender switcher — team members are scoped to their tender */}
        <div style={{ position: "relative", marginBottom: 14 }}>
          <button className="panel row between" onClick={() => !isTeam && setSwitcher((s) => !s)} style={{ width: "100%", padding: "10px 12px", cursor: isTeam ? "default" : "pointer", background: "var(--card)" }}>
            <span className="row" style={{ gap: 10, minWidth: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: 8, background: `hsl(${tender?.hue ?? 20} 45% 55%)`, flexShrink: 0 }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontWeight: 500, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tender?.name}</span>
                <span className="dim" style={{ fontSize: 11 }}>{tender?.code}</span>
              </span>
            </span>
            <Icon.swap width={15} style={{ color: "var(--ink-3)", flexShrink: 0 }} />
          </button>
          {switcher && (
            <div className="card" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 30, padding: 6, boxShadow: "var(--shadow-lg)" }}>
              {tenders.map((t) => (
                <button key={t.id} onClick={() => { nav(`/manage/${t.id}/${section}`); setSwitcher(false); }} className="row" style={{ width: "100%", gap: 10, padding: "9px 10px", border: "none", background: t.id === tender?.id ? "var(--paper-2)" : "transparent", borderRadius: 9, cursor: "pointer", textAlign: "left" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 8, background: `hsl(${t.hue} 45% 55%)` }} />
                  <span style={{ flex: 1 }}><span style={{ display: "block", fontSize: 13, fontWeight: 500 }}>{t.name}</span><span className="dim" style={{ fontSize: 10.5 }}>{t.code} · {t.location}</span></span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* nav */}
        <nav className="col" style={{ gap: 2, flex: 1, overflow: "auto" }}>
          {SECTIONS.map((s) => (
            <NavLink key={s.key} to={`/manage/${tender?.id}/${s.key}`} className="navlink">
              {({ isActive }) => (
                <span className="row between" style={{ width: "100%" }}>
                  <span className="row" style={{ gap: 11 }}>{Icon[s.icon]({ width: 18, style: { color: isActive ? "var(--accent-ink)" : "var(--ink-3)" } })}<span>{s.label}</span></span>
                  {s.key === "rfis" && rfiCount > 0 && <span className="navcount">{rfiCount}</span>}
                  {s.key === "results" && readyToOpen && <span className="navcount warn">!</span>}
                  {s.key === "bidders" && tender && <span className="dim mono" style={{ fontSize: 10.5 }}>{submittedCount(tender)}/{tender.bidders.filter(b => b.status !== "declined").length}</span>}
                  {s.key === "submissions" && tender && submittedCount(tender) > 0 && <span className="navcount" style={{ background: "var(--accent)" }}>{submittedCount(tender)}</span>}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* footer */}
        <div className="col" style={{ gap: 8, marginTop: 12 }}>
          <div className="row" style={{ justifyContent: "center" }}><SyncBadge /></div>
          {!isTeam && <>
            <button className="panel row between" onClick={() => tender && nav(`/client/${tender.id}`)} style={{ padding: "9px 12px", cursor: "pointer", background: "var(--card)", textAlign: "left" }}>
              <span className="row" style={{ gap: 10 }}><Icon.arrowUpRight width={15} style={{ color: "var(--accent-ink)" }} /><span style={{ fontSize: 12.5 }}>Client view</span></span>
            </button>
            <button className="panel row between" onClick={() => tender && nav(`/bid/${tender.id}`)} style={{ padding: "9px 12px", cursor: "pointer", background: "var(--card)", textAlign: "left" }}>
              <span className="row" style={{ gap: 10 }}><Icon.arrowUpRight width={15} style={{ color: "var(--sky)" }} /><span style={{ fontSize: 12.5 }}>Bidder view</span></span>
            </button>
          </>}
          <div className="row between" style={{ marginTop: 4 }}>
            <div className="row" style={{ gap: 9 }}>
              <Avatar name={session?.role === "bidteam" ? session.name : STUDIO.manager} hue={20} size={30} />
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>{session?.role === "bidteam" ? session.name : STUDIO.manager}</div>
                <div className="dim" style={{ fontSize: 10.5 }}>{level !== "none" ? BID_LEVEL_LABEL[level] : "Tender manager"}</div>
              </div>
            </div>
            <div className="row" style={{ gap: 6 }}>
              <button className="iconbtn" onClick={toggleTheme} style={{ width: 32, height: 32 }} aria-label="Toggle theme">{theme === "light" ? <Icon.moon width={16} /> : <Icon.sun width={16} />}</button>
              <button className="iconbtn" onClick={() => { logout(); nav("/"); }} style={{ width: 32, height: 32 }} aria-label="Sign out"><Icon.logout width={16} /></button>
            </div>
          </div>
        </div>
      </aside>

      {/* ---------------- main ---------------- */}
      <main style={{ minWidth: 0, background: "var(--paper)" }}>
        {children}
      </main>

      {/* ---------------- phone chrome: top bar + thumb tabs + "More" sheet ---------------- */}
      <div className="m-top">
        <button className="row" onClick={() => !isTeam && setMore(true)} style={{ gap: 10, background: "none", border: "none", cursor: "pointer", minWidth: 0, padding: 0, textAlign: "left" }}>
          <span style={{ width: 30, height: 30, borderRadius: 9, background: "var(--ink)", color: "var(--paper)", display: "grid", placeItems: "center", flexShrink: 0 }}><Icon.logo width={17} /></span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tender?.name}</span>
            <span className="dim" style={{ fontSize: 10.5 }}>{tender?.code} · Bid Manager</span>
          </span>
        </button>
        <span className="row" style={{ gap: 6, flexShrink: 0 }}><SyncBadge /></span>
      </div>

      <nav className="m-tabs">
        {MOBILE_TABS.map((s) => {
          const active = section === s.key;
          return (
            <button key={s.key} className="m-tab" onClick={() => nav(`/manage/${tender?.id}/${s.key}`)} style={{ color: active ? "var(--accent-ink)" : "var(--ink-3)" }}>
              <span style={{ position: "relative" }}>
                {Icon[s.icon]({ width: 21 })}
                {s.key === "rfis" && rfiCount > 0 && <span className="m-dot">{rfiCount}</span>}
              </span>
              <span style={{ fontSize: 10, fontWeight: active ? 600 : 500 }}>{s.short}</span>
            </button>
          );
        })}
        <button className="m-tab" onClick={() => setMore(true)} style={{ color: more ? "var(--accent-ink)" : "var(--ink-3)" }}>
          <span style={{ position: "relative" }}>
            <Icon.layers width={21} style={{ transform: "rotate(90deg)" }} />
            {readyToOpen && <span className="m-dot">!</span>}
          </span>
          <span style={{ fontSize: 10, fontWeight: 500 }}>More</span>
        </button>
      </nav>

      {more && (
        <div onClick={() => setMore(false)} style={{ position: "fixed", inset: 0, zIndex: 80, background: "rgba(20,14,8,.42)", display: "grid", alignItems: "end", animation: "floatIn .2s ease" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--paper)", borderRadius: "22px 22px 0 0", border: "1px solid var(--line)", borderBottom: "none", padding: "10px 18px calc(18px + env(safe-area-inset-bottom))", maxHeight: "82vh", overflow: "auto" }}>
            <div style={{ width: 36, height: 4, borderRadius: 4, background: "var(--line)", margin: "0 auto 12px" }} />
            {!isTeam && tenders.length > 1 && (
              <>
                <div className="kicker" style={{ margin: "6px 0 8px" }}>Switch tender</div>
                <div className="col" style={{ gap: 4, marginBottom: 14 }}>
                  {tenders.map((t) => (
                    <button key={t.id} className="row" onClick={() => { setMore(false); nav(`/manage/${t.id}/${section}`); }} style={{ gap: 10, padding: "9px 10px", border: "none", background: t.id === tender?.id ? "var(--paper-2)" : "transparent", borderRadius: 9, cursor: "pointer", textAlign: "left", width: "100%" }}>
                      <span style={{ width: 8, height: 8, borderRadius: 8, background: `hsl(${t.hue} 45% 55%)`, flexShrink: 0 }} />
                      <span style={{ flex: 1, minWidth: 0 }}><span style={{ display: "block", fontSize: 13.5, fontWeight: 500 }}>{t.name}</span><span className="dim" style={{ fontSize: 10.5 }}>{t.code} · {t.location}</span></span>
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="kicker" style={{ margin: "6px 0 8px" }}>Everything in this tender</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 14 }}>
              {SECTIONS.map((s) => (
                <button key={s.key} className="row" onClick={() => { setMore(false); nav(`/manage/${tender?.id}/${s.key}`); }} style={{ gap: 9, padding: "11px 10px", border: "1px solid var(--line)", borderRadius: 11, background: section === s.key ? "var(--paper-2)" : "var(--card)", cursor: "pointer", textAlign: "left", fontSize: 12.5, minWidth: 0 }}>
                  {Icon[s.icon]({ width: 16, style: { color: section === s.key ? "var(--accent-ink)" : "var(--ink-3)", flexShrink: 0 } })}
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</span>
                  {s.key === "rfis" && rfiCount > 0 && <span className="navcount">{rfiCount}</span>}
                  {s.key === "results" && readyToOpen && <span className="navcount warn">!</span>}
                </button>
              ))}
            </div>
            <div className="row wrap" style={{ gap: 8 }}>
              {!isTeam && <button className="btn ghost sm" onClick={() => { setMore(false); nav("/pm"); }}><Icon.dashboard width={13} />Project Manager</button>}
              {!isTeam && <button className="btn ghost sm" onClick={() => { setMore(false); if (tender) nav(`/client/${tender.id}`); }}><Icon.arrowUpRight width={13} />Client view</button>}
              <button className="btn ghost sm" onClick={toggleTheme}>{theme === "light" ? <Icon.moon width={13} /> : <Icon.sun width={13} />}{theme === "light" ? "Dark" : "Light"}</button>
              <button className="btn ghost sm" onClick={() => { logout(); nav("/"); }}><Icon.logout width={13} />Sign out</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
